import {
  MAX_LENGTH_MESSAGE,
  REQUIRED_MESSAGE,
  requiredNumber,
  requiredString,
} from '@wyz/utils';
import { isNil } from 'lodash-es';
import { z } from 'zod';
import { COMPUTED_PRICING_POLICY_STATUS } from '~/common/schemas';
import {
  diameterSchema,
  MISSING_DIAMETER,
  OVERLAP_DIAMETER,
  PP_CUSTOM_CONSTRAINT,
  PRICING_POLICY_STATUS,
  validateRanges,
} from '~/purchase/schemas';
import { IDiameter } from '~/purchase/types';

export const TYRE_COUNT_ENUM = {
  ONE_TYRE: 1,
  TWO_TYRE: 2,
  FOUR_TYRE: 4,
} as const;
export const RIM_TYPE_ENUM = {
  ALLOY: 'ALLOY', // alliage
  STEEL: 'STEEL', // TOle
} as const;
export const PRICE_TYPE_ENUM = {
  TAX_INCLUDED: 'TAX_INCLUDED',
  TAX_EXCLUDED: 'TAX_EXCLUDED',
} as const;

export const SERVICE_TYPE = {
  ONE_TYRE_DELIVERY: 'ONE_TYRE_DELIVERY',
  MOUNTING: 'MOUNTING',
  CUSTOM: 'CUSTOM',
  GEOMETRY: 'GEOMETRY',
} as const;

export const quoteServiceMountingStepFormSchema = z
  .object({
    name: requiredString.max(
      PP_CUSTOM_CONSTRAINT.NAME_MAX_LENGTH,
      MAX_LENGTH_MESSAGE,
    ),
    vehicleTypeGroup: requiredString,
    refinementFlags: z.object({
      rim: z.boolean(),
      tyreType: z.boolean(),
      diameter: z.boolean(),
      tyreCount: z.boolean(),
    }),
    priceType: z.enum([
      PRICE_TYPE_ENUM.TAX_INCLUDED,
      PRICE_TYPE_ENUM.TAX_EXCLUDED,
    ]),
    globalValue: z.number().nullish(),
    rims: z
      .record(
        z.string(),
        z.object({
          refinements: z
            .array(
              z
                .object({
                  diameter: diameterSchema,
                  tyreCount: z.record(
                    z.string(),
                    z.object({
                      tyreTypes: z.record(
                        z.string(),
                        z.object({
                          value: requiredNumber,
                        }),
                      ),
                    }),
                  ),
                })
                .nullish(),
            )
            .nullish(),
        }),
      )
      .nullish(),
  })
  .superRefine((formData, ctx) => {
    // Conditional validation
    const hasRefinement =
      formData.refinementFlags.diameter ||
      formData.refinementFlags.rim ||
      formData.refinementFlags.tyreCount ||
      formData.refinementFlags.tyreType;

    if (!hasRefinement) {
      if (isNil(formData.globalValue)) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['globalValue'],
          fatal: true,
        });
      }
    } else {
      if (!formData.rims) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['rims'],
          fatal: true,
        });
      }
      if (formData.refinementFlags.diameter) {
        Object.keys(formData.rims || {}).forEach((rimCodeName) => {
          const refinements = formData.rims?.[rimCodeName].refinements || [];
          const diameters: Array<IDiameter> = refinements
            .filter((refinement) => refinement?.diameter)
            .map((refinement) => refinement?.diameter as IDiameter);

          const diameterValidation = validateRanges(diameters);
          if (diameters.length > 0) {
            if (diameterValidation.missing) {
              ctx.addIssue({
                code: 'custom',
                message: MISSING_DIAMETER,
                path: ['rims', rimCodeName, 'refinements'],
                fatal: true,
              });
            }
            if (diameterValidation.overlap) {
              ctx.addIssue({
                code: 'custom',
                message: OVERLAP_DIAMETER,
                path: ['rims', rimCodeName, 'refinements'],
                fatal: true,
              });
            }
          }
        });
      }
    }
  });

export const quoteServiceGeometryStepFormSchema = z.object({
  geometry: z.object({
    tyreCounts: z.record(
      z.string(),
      z.object({
        tyreTypes: z.record(z.string(), z.object({ value: requiredNumber })),
        code: requiredString.max(
          PP_CUSTOM_CONSTRAINT.SERVICE_CODE_LENGTH,
          MAX_LENGTH_MESSAGE,
        ),
        description: requiredString.max(
          PP_CUSTOM_CONSTRAINT.SERVICE_DESCRIPTION_LENGTH,
          MAX_LENGTH_MESSAGE,
        ),
      }),
    ),
  }),
  refinementFlags: z.object({
    tyreType: z.boolean(),
  }),
  hasCustomerServices: z.boolean().default(false).optional(),
  customServices: z
    .array(
      z.object({
        code: z.string(),
        description: z.string(),
        price: z.number().nullish(),
        active: z.boolean(),
      }),
    )
    .superRefine((services, ctx) => {
      services.forEach((service, index) => {
        if (
          index === 0 &&
          !service.code.trim() &&
          !service.description.trim() &&
          !service.price
        ) {
          return; // Skip validation for the first service
        }

        if (!service.code.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: REQUIRED_MESSAGE,
            path: [index, 'code'],
          });
        }

        if (!service.description.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: REQUIRED_MESSAGE,
            path: [index, 'description'],
          });
        }

        if (
          service.price === undefined ||
          service.price === null ||
          isNaN(service.price)
        ) {
          ctx.addIssue({
            code: 'custom',
            message: REQUIRED_MESSAGE,
            path: [index, 'price'],
          });
        }
      });
    }),
});

export const quoteServicePricingApplicationStepFormSchema = z.object({
  startDate: z.date({
    error: (issue) =>
      issue.input === undefined ? REQUIRED_MESSAGE : undefined,
  }),
  endDate: z.date().nullish(),
});

export const quoteDefaultServicePricingApplicationStepFormSchema =
  quoteServicePricingApplicationStepFormSchema.extend({
    customers: z.array(z.string()).min(1, REQUIRED_MESSAGE),
  });

export const quoteServicePricingPolicySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  updatedAt: z.date().nullable(),
  suppliers: z.array(z.object({ name: z.string() })),
  brands: z.array(z.object({ name: z.string() })),
  startDate: z.date(),
  endDate: z.date().nullable(),
  status: z.enum(PRICING_POLICY_STATUS),
  computedStatus: z.enum(COMPUTED_PRICING_POLICY_STATUS),
  formattedStartDate: z.string(),
  formattedEndDate: z.string().nullable(),
  createdAt: z.date().nullable(),
  policyGroupId: z.number(),
  default: z.boolean(),
  customerCriteria: z.array(
    z.object({
      id: z.string(),
      criterion: z.string(),
      code: z.string(),
      value: z.string(),
    }),
  ),
});

export const QUOTE_SERVICE_PRICING_POLICY_FORM_STEPS = [
  'APPLICATION',
  'MOUNTING',
  'GEOMETRY_SERVICE',
] as const;
